import * as types from 'shrike-client';

import { init } from 'snabbdom';
// const snabbdom = require('snabbdom');
import { VNode, VNodeData } from 'snabbdom/vnode';
import { h, VNodeChildElement } from 'snabbdom/h';
import styleMod, { VNodeStyle } from 'snabbdom/modules/style';
import eventListenersModule, { On } from 'snabbdom/modules/eventlisteners';
import attributesModule from 'snabbdom/modules/attributes';
import propsModule from 'snabbdom/modules/props';

function assertUnreachable(x: never) {
    console.error('reached unreachable:', x);
}

const FONT_SCALE = 15;

export default class Graphics {
    private root: HTMLElement;
    private width: number;
    private height: number;

    private vnode?: VNode;

    private patch: (oldVnode: Element | VNode, vnode: VNode) => VNode;
    private gfxNode?: VNode;

    constructor(root: HTMLElement, width: number, height: number) {
        this.width = width;
        this.height = height;

        this.root = document.createElement('div');
        root.appendChild(this.root);
        this.patch = init([styleMod, eventListenersModule, attributesModule, propsModule]);
    }

    public resize(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.rerender();
    }

    public render(gfx: types.Graphics, eventHandler: (ev: types.Event) => void) {
        this.gfxNode = this.renderGraphics([], gfx, eventHandler);
        this.rerender();
    }

    public rerender() {
        const vnode = h('div', {
            style: {
                position: 'absolute',
                overflow: 'hidden',
                width: this.width + 'px',
                height: this.height + 'px',
            },
        }, this.gfxNode);

        if (!this.vnode) {
            this.vnode = this.patch(this.root, vnode);
        } else {
            this.vnode = this.patch(this.vnode, vnode);
        }
    }

    private renderGraphics(
        path: number[],
        gfx: types.Graphics,
        eventHandler: (ev: types.Event) => void): VNode {

        let events;
        let attrs;
        switch (gfx.tag) {
            case 'Container':
                const children = gfx.fields[0] as [types.Graphics];
                events = gfx.fields[1] as [types.ContainerEvent];
                attrs = gfx.fields[2] as [types.ContainerAttribute];
                return this.renderContainer(path, children, events, attrs, eventHandler);
            case 'Image':
                const url = gfx.fields[0] as string;
                events = gfx.fields[1] as [types.ImageEvent];
                attrs = gfx.fields[2] as [types.ImageAttribute];
                return this.renderImage(path, url, events, attrs, eventHandler);
            case 'Text':
                const txt = gfx.fields[0] as string;
                events = gfx.fields[1] as [types.TextEvent];
                attrs = gfx.fields[2] as [types.TextAttribute];
                return this.renderText(path, txt, events, attrs, eventHandler);
            case 'Input':
                events = gfx.fields[0] as [types.InputEvent];
                attrs = gfx.fields[1] as [types.InputAttribute];
                return this.renderInput(path, events, attrs, eventHandler);
            case 'Chat':
                const chatMsg = gfx.fields[0] as string;
                const chatHistory = gfx.fields[1] as [string];
                events = gfx.fields[2] as [types.ChatEvent];
                attrs = gfx.fields[3] as [types.ChatAttribute];
                return this.renderChat(path, chatMsg, chatHistory, events, attrs, eventHandler);
            case 'Button':
                const text = gfx.fields[0] as string;
                events = gfx.fields[1] as [types.ButtonEvent];
                attrs = gfx.fields[2] as [types.ButtonAttribute];
                return this.renderButton(path, text, events, attrs, eventHandler);
        }
        assertUnreachable(gfx);
    }

    private renderContainer(
        path: number[],
        children: [types.Graphics],
        events: types.ContainerEvent[],
        attrs: [types.ContainerAttribute],
        eventHandler: (ev: types.Event) => void): VNode {

        for (const event of events) {
            console.error('invalid event:', event);
        }

        const style: Record<string, string> = {
            'position': 'absolute',
            // left: '50%', // these are set lower down
            // top: '50%', // these are set lower down
            'width': '100%',
            'height': '100%',
            'transform': 'translate(-50%, -50%)',
            'pointer-events': 'none',
        };

        let pos = [0, 0];
        let align = [0.5, 0.5];
        for (const attr of attrs) {
            switch (attr.tag) {
                case 'Position':
                    pos = attr.fields;
                    continue;
                case 'Align':
                    align = attr.fields;
                    continue;
                case 'Size':
                    style.width = Math.round(attr.fields[0] * 100) + '%';
                    style.height = Math.round(attr.fields[1] * 100) + '%';
                    continue;
                case 'BackgroundColor':
                    style['background-color'] = `rgba(${attr.fields[0]}, ${attr.fields[1]}, ${attr.fields[2]}, ${attr.fields[3]})`;
                    continue;
            }
            assertUnreachable(attr);
        }
        style.left = `calc(${Math.round(align[0] * 100)}% + ${pos[0]}px)`;
        style.top = `calc(${Math.round(100 - align[1] * 100)}% - ${pos[1]}px)`;


        const data: VNodeData = {
            style,
        };

        const childVNodes: VNodeChildElement[] = [];
        for (const [childIdx, child] of children.entries()) {
            childVNodes.push(this.renderGraphics([...path, childIdx], child, eventHandler));
        }

        return h('div', data, childVNodes);
    }
    private renderImage(
        path: number[],
        url: string,
        events: types.ImageEvent[],
        attrs: [types.ImageAttribute],
        eventHandler: (ev: types.Event) => void): VNode {

        const style: Record<string, string> = {
            'position': 'absolute',
            'left': '50%',
            'top': '50%',
            'transform': 'translate(-50%, -50%)',
            'pointer-events': 'auto',
        };
        const handler: On = {};

        for (const [eventIdx, event] of events.entries()) {
            switch (event.tag) {
                case 'OnClick':
                    style.cursor = 'pointer';
                    handler.click = (evt: MouseEvent) => {
                        eventHandler({
                            event_idx: eventIdx,
                            event_type: 'OnClick',
                            args: [],
                            path,
                        });
                    };
                    continue;
            }
            console.error('invalid event:', event);
        }

        for (const attr of attrs) {
            switch (attr.tag) {
                case 'Scale':
                    style.transform += ` scale(${attr.fields[0]}, ${attr.fields[1]})`;
                    continue;
            }
            console.error('invalid attr:', attr);
        }

        const data: VNodeData = {
            attrs: {
                src: url,
            },
            hook: {
                update: (oldVnode, vnode) => {
                    if ((oldVnode.sel === vnode.sel) && oldVnode.sel === 'img') {
                        vnode.data!!.style!!.visibility = 'hidden';
                        vnode.data!!.on!!.load = () => {
                            (vnode.elm as HTMLImageElement).style.visibility = 'visible';
                        };
                    }
                },
            },
            on: handler,
            style,
        };
        return h('img', data);
    }
    private renderText(
        path: number[],
        text: string,
        events: types.TextEvent[],
        attrs: [types.TextAttribute],
        eventHandler: (ev: types.Event) => void): VNode {

        const style: Record<string, string> = {
            'white-space': 'nowrap',
            'position': 'absolute',
            'left': '50%',
            'top': '50%',
            'transform': 'translate(-50%, -50%)',
            'font-size': (25 / FONT_SCALE) + 'vmax',
        };
        const handler: On = {};

        for (const [eventIdx, event] of events.entries()) {
            switch (event.tag) {
                case 'OnClick':
                    style.cursor = 'pointer';
                    style['pointer-events'] = 'auto';
                    handler.click = (evt: MouseEvent) => {
                        eventHandler({
                            event_idx: eventIdx,
                            event_type: 'OnClick',
                            args: [],
                            path,
                        });
                    };
                    continue;
            }
            console.error('invalid event:', event);
        }
        for (const attr of attrs) {
            switch (attr.tag) {
                case 'FontSize':
                    style['font-size'] = (attr.fields / FONT_SCALE) + 'vmax';
                    continue;
                case 'Color':
                    style.color = `rgba(${attr.fields[0]}, ${attr.fields[1]}, ${attr.fields[2]}, ${attr.fields[3]})`;
                    continue;
            }
            console.error('invalid attr:', attr);
        }
        const data: VNodeData = {
            style,
            on: handler,
        };

        return h('span', data, text);
    }
    private renderInput(
        path: number[],
        events: types.InputEvent[],
        attrs: [types.InputAttribute],
        eventHandler: (ev: types.Event) => void): VNode {

        // const input = new PIXI.Text('<input>');
        // for (const event of events) {
        //     console.error('invalid event:', event);
        // }
        // for (const attr of attrs) {
        //     switch (attr.tag) {
        //         case 'FontSize':
        //             input.style.fontSize = attr.fields;
        //             continue;
        //     }
        //     console.error('invalid attr:', attr);
        // }
        // return input;
        throw new Error('input unimplemented');
    }

    private renderChat(
        path: number[],
        message: string,
        history: string[],
        events: types.ChatEvent[],
        attrs: [types.ChatAttribute],
        eventHandler: (ev: types.Event) => void): VNode {

        const style: Record<string, string> = {
            'white-space': 'nowrap',
            'position': 'absolute',
            'left': '50%',
            'top': '50%',
            'transform': 'translate(-50%, -50%)',
            'font-size': '25min',
            'width': '30%',
        };
        const inputHandler: On = {};
        const handler: On = {};
        const randId = 'chat-' + Math.random();

        for (const [eventIdx, event] of events.entries()) {
            switch (event.tag) {
                case 'OnSend':
                    // style.cursor = 'pointer';
                    // style['pointer-events'] = 'auto';
                    handler.click = (evt: MouseEvent) => {
                        const input = document.getElementById(randId) as HTMLInputElement;
                        if (input.value !== '') {
                            eventHandler({
                                event_idx: eventIdx,
                                event_type: 'OnSend',
                                // args: [message],
                                args: [input.value],
                                path,
                            });
                            input.value = '';
                        }
                    };
                    inputHandler.keyup = (evt: KeyboardEvent) => {
                        if (evt.key === 'Enter') {
                            const input = document.getElementById(randId) as HTMLInputElement;
                            if (input.value !== '') {
                                eventHandler({
                                    event_idx: eventIdx,
                                    event_type: 'OnSend',
                                    // args: [message],
                                    args: [input.value],
                                    path,
                                });
                                input.value = '';
                            }
                        }
                    };
                    continue;
            }
            console.error('invalid event:', event);
        }
        for (const attr of attrs) {
            switch (attr.tag) {
                case 'FontSize':
                    style['font-size'] = (attr.fields / FONT_SCALE) + 'vmax';
                    continue;
            }
            console.error('invalid attr:', attr);
        }

        const msgs = history.reverse().map((s) => h('li', {
            style: {
                'padding-left': '2rem',
                'text-indent': '-2rem',
            },
        }, s));
        const msgUl = h('ul', {
            style: {
                'margin': '0',
                'padding': '0',
                'list-style-type': 'none',
                'display': 'flex',
                'flex-direction': 'column-reverse',
                'white-space': 'normal',
            },
        }, msgs);
        const msgDiv = h('div', {
            style: {
                'flex': '1',
                'height': '150px',
                'overflow': 'auto',
                'pointer-events': 'auto',
                'display': 'flex',
                'flex-direction': 'column',
                'background-color': 'rgb(220, 220, 220)',
            },
        }, msgUl);

        const chatData: VNodeData = {
            props: {
                value: message,
                id: randId,
            },
            style: {
                'pointer-events': 'auto',
                'width': '70%',
            },
            on: inputHandler,
        };
        const chat = h('input', chatData);

        const chatSendData: VNodeData = {
            style: {
                'pointer-events': 'auto',
                'width': '25%',
            },
            on: handler,
        };
        const chatSend = h('button', chatSendData, 'Send');

        const data: VNodeData = {
            style,
        };
        return h('div', data, [msgDiv, chat, chatSend]);
    }

    private renderButton(
        path: number[],
        text: string,
        events: types.ButtonEvent[],
        attrs: [types.ButtonAttribute],
        eventHandler: (ev: types.Event) => void): VNode {

        const style: Record<string, string> = {
            'white-space': 'nowrap',
            'position': 'absolute',
            'left': '50%',
            'top': '50%',
            'transform': 'translate(-50%, -50%)',
            'font-size': (25 / FONT_SCALE) + 'vmax',
        };
        const handler: On = {};

        for (const [eventIdx, event] of events.entries()) {
            switch (event.tag) {
                case 'OnClick':
                    style.cursor = 'pointer';
                    style['pointer-events'] = 'auto';
                    handler.click = (evt: MouseEvent) => {
                        eventHandler({
                            event_idx: eventIdx,
                            event_type: 'OnClick',
                            args: [],
                            path,
                        });
                    };
                    continue;
            }
            console.error('invalid event:', event);
        }
        for (const attr of attrs) {
            switch (attr.tag) {
                case 'FontSize':
                    style['font-size'] = (attr.fields / FONT_SCALE) + 'vmax';
                    continue;
                case 'Color':
                    style.color = `rgba(${attr.fields[0]}, ${attr.fields[1]}, ${attr.fields[2]}, ${attr.fields[3]})`;
                    continue;
            }
            console.error('invalid attr:', attr);
        }
        const data: VNodeData = {
            style,
            on: handler,
        };

        return h('button', data, text);
    }
}
